import { useState, CSSProperties, Fragment } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import AddBoxIcon from '@mui/icons-material/AddBox'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ArchiveIcon from '@mui/icons-material/Archive'
import isPropValid from '@emotion/is-prop-valid'
import { useNavigate, useLoaderData, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useEventsTypeOptions, getOption } from '../../utils'
import { Events, EventStatus, LoaderData } from '../../types'
import BreadCrumbs from '../root/components/BreadCrumbs'
import IconButtonOutlined from '../root/components/IconButtonOutlined'
import SubHeader from '../root/components/SubHeader'
import ParticipantStatusTag from '../events/components/ParticipantStatusTag'
import EventInfoBox from '../events/components/EventInfoBox'
import EventStatusTag from '../events/components/EventStatusTag'
import { StatusTag } from '../root/components/StatusTag'
import { dashboardLoader } from './Dashboard.loader'
import { isCurrentUserStaff, resetEventDetails } from '../../store.slice'
import { useAppDispatch, useAppSelector } from '../../store'

interface CardProps {
  color?: string
  active?: boolean
  maxWidth?: CSSProperties['maxWidth']
}
const StyledCard = styled('a', {
  shouldForwardProp: prop => typeof prop === 'string' && isPropValid(prop) && prop !== 'active',
})<CardProps>(({ theme, color, active, maxWidth = '520px' }) => ({
  padding: '1rem',
  backgroundColor: theme.colors.secondaryBackground,
  borderRadius: '20px',
  boxShadow: theme.boxShadows.main,
  borderColor: active ? theme.colors.primary : theme.colors.secondary,
  borderTopWidth: '5px',
  borderTopColor: active ? undefined : color,
  color: theme.palette.text.primary,
  borderStyle: 'solid',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0 auto',
  maxWidth,
  minHeight: '200px',
  cursor: 'pointer',
  position: 'relative',
  gap: '1rem',
  overflow: 'hidden',
  textDecoration: 'none',

  ':visited': {
    color: 'inherit',
  },

  ':hover': {
    boxShadow: theme.boxShadows.modal,
    borderColor: theme.colors.primary,
  },
}))
export const Card = ({ children, ...props }: React.ComponentProps<typeof StyledCard>) => (
  <StyledCard role="button" {...props}>
    {children}
  </StyledCard>
)

const HelpBar = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.whiteBackground,
  borderRadius: '20px',
  margin: '1rem 1rem 0',
  padding: '0.5rem',
}))
const TagHelper = styled('div')(({ theme, color }) => ({
  borderRadius: '100%',
  padding: '0.5rem',
  width: '2rem',
  height: '2rem',
  border: `3px solid ${theme.palette.common.white}`,
  boxShadow: theme.boxShadows.main,
  backgroundColor: color,
}))

function EventsCard({ event, isPast }: { event: Events; isPast?: boolean }) {
  const navigate = useNavigate()
  const location = useLocation()
  const isPublic = location.pathname.startsWith('/public')
  const { t } = useTranslation('dashboard')
  const eventsTypeOptions = useEventsTypeOptions(t)
  const type = getOption(eventsTypeOptions, undefined, event.type)

  return (
    <Card
      key={event.id}
      onClick={e => {
        e.preventDefault()
        navigate(isPublic ? `/public/events/${event.id}` : `/events/${event.id}`)
      }}
      href={isPublic ? `/public/events/${event.id}` : `/events/${event.id}`}
      color={type?.color}
      sx={theme => ({
        minWidth: { xs: 'calc(100% - 4rem)', sm: '350px', md: '400px' },
        opacity: event.status === EventStatus.Draft || isPast ? 0.75 : 1,
        background: theme.palette.grey[200],
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="-webkit-fill-available"
      >
        <h4>{event.name}</h4>
        <Chip
          color="primary"
          sx={{
            backgroundColor: type?.color,
          }}
          label={type?.label}
        />
      </Stack>
      <EventInfoBox
        start={event.start}
        end={event.end}
        location={event.location}
        direction="column"
      />
      <div
        style={{
          width: '100%',
          height: '200px',
          objectFit: 'cover',
          position: 'relative',
          backgroundColor: '#D9D9D9',
          backgroundImage: event.picture ? `url(${event.picture})` : undefined,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          borderRadius: '10px',
        }}
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {event.registration_status ? (
          <ParticipantStatusTag status={event.registration_status} absolute position="bottom" />
        ) : event.status !== EventStatus.Published ? (
          <EventStatusTag status={event.status} absolute position="bottom" />
        ) : (
          <StatusTag absolute size="medium" color="#adafb6" position="bottom">
            {t('Inscriptions à venir')}
          </StatusTag>
        )}
      </div>
    </Card>
  )
}

function Dashboard() {
  const navigate = useNavigate()
  const { t } = useTranslation('dashboard')
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [mode, setMode] = useState<'calendar' | 'archive'>('calendar')
  const [openPast, setOpenPast] = useState(true)
  const [openOngoing, setOpenOngoing] = useState(true)
  const [openFuture, setOpennFuture] = useState(true)

  const eventsTypeOptions = useEventsTypeOptions(t)
  const { ongoing, future, past, archived } = useLoaderData() as LoaderData<
    ReturnType<typeof dashboardLoader>
  >

  const totalLength = ongoing.length + future.length + past.length

  const isStaff = useAppSelector(isCurrentUserStaff)

  return (
    <>
      <SubHeader>
        <BreadCrumbs links={[{ name: t('Événements'), pathname: '' }]} />

        {isStaff ? (
          <Button
            startIcon={<AddBoxIcon />}
            variant="contained"
            onClick={e => {
              e.preventDefault()
              dispatch(resetEventDetails('new'))
              navigate('/admin/events/new/event')
            }}
            href="/admin/events/new/event"
          >
            {isMobile ? t('Événement') : t('Nouvel événement')}
          </Button>
        ) : (
          <div />
        )}
      </SubHeader>

      <HelpBar direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Tooltip title={t('Calendrier')}>
            <IconButton
              color={mode === 'calendar' ? 'primary' : 'inherit'}
              onClick={() => {
                setMode('calendar')
              }}
            >
              <CalendarMonthIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Archives')}>
            <IconButton
              color={mode === 'archive' ? 'primary' : 'inherit'}
              onClick={() => {
                setMode('archive')
              }}
            >
              <ArchiveIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="center" gap="0.5rem">
          {!isMobile &&
            eventsTypeOptions.map(option => (
              <Fragment key={option.value}>
                <TagHelper color={option.color} />
                <Typography variant="body2">{option.label}</Typography>
              </Fragment>
            ))}
        </Stack>
      </HelpBar>

      <Stack
        flexDirection="row"
        alignContent="start"
        justifyContent="start"
        gap="1rem"
        padding="1rem"
        flex="1 0"
        flexWrap="wrap"
        style={{ overflowY: 'auto' }}
      >
        {mode === 'archive' && (
          <Stack
            direction="column"
            width="100%"
            style={{
              borderRadius: '20px',
              border: `2px solid ${theme.colors.secondary}`,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              padding="1rem"
              style={{
                borderBottom: `2px solid ${theme.colors.secondary}`,
                backgroundColor: theme.colors.whiteBackground,
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                {t('Événements archivés')}
              </Typography>
              <IconButtonOutlined onClick={() => null} disabled sx={{ opacity: 0 }} />
            </Stack>

            <Stack
              direction="row"
              flexWrap="wrap"
              gap="1rem"
              padding={'1rem'}
              style={{
                backgroundColor: theme.colors.greyBackground,
              }}
            >
              {archived.map(event => (
                <EventsCard key={event.id} event={event} />
              ))}
            </Stack>
          </Stack>
        )}
        {mode === 'calendar' && (
          <>
            {totalLength === 0 ? (
              <h1>{t("Bienvenue sur l'application de gestion des événements de la SFCTCV")}</h1>
            ) : (
              <>
                {ongoing.length > 0 && (
                  <Stack
                    direction="column"
                    width="100%"
                    style={{
                      borderRadius: '20px',
                      border: `2px solid ${theme.colors.secondary}`,
                      overflow: 'hidden',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      padding="1rem"
                      style={{
                        borderBottom: `2px solid ${theme.colors.secondary}`,
                        backgroundColor: theme.colors.whiteBackground,
                      }}
                    >
                      <Typography variant="h4" fontWeight={600}>
                        {t('Événements en cours')}
                      </Typography>
                      <IconButtonOutlined onClick={() => setOpenOngoing(!openOngoing)}>
                        {openOngoing ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButtonOutlined>
                    </Stack>
                    {openOngoing && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap="1rem"
                        padding={'1rem'}
                        style={{
                          backgroundColor: theme.colors.tertiaryBackground,
                        }}
                      >
                        {ongoing.map(event => (
                          <EventsCard key={event.id} event={event} />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                )}
                {future.length > 0 && (
                  <Stack
                    direction="column"
                    width="100%"
                    style={{
                      borderRadius: '20px',
                      border: `2px solid ${theme.colors.secondary}`,
                      overflow: 'hidden',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      padding="1rem"
                      style={{
                        borderBottom: `2px solid ${theme.colors.secondary}`,
                        backgroundColor: theme.colors.whiteBackground,
                      }}
                    >
                      <Typography variant="h4" fontWeight={600}>
                        {t('Événements à venir')}
                      </Typography>
                      <IconButtonOutlined onClick={() => setOpennFuture(!openFuture)}>
                        {openFuture ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButtonOutlined>
                    </Stack>
                    {openFuture && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap="1rem"
                        padding={'1rem'}
                        style={{
                          backgroundColor: theme.colors.secondaryBackground,
                        }}
                      >
                        {future.map(event => (
                          <EventsCard key={event.id} event={event} />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                )}
                {past.length > 0 && (
                  <Stack
                    direction="column"
                    width="100%"
                    style={{
                      borderRadius: '20px',
                      border: `2px solid ${theme.colors.secondary}`,
                      overflow: 'hidden',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      padding="1rem"
                      style={{
                        borderBottom: `2px solid ${theme.colors.secondary}`,
                        backgroundColor: theme.colors.whiteBackground,
                      }}
                    >
                      <Typography variant="h4" fontWeight={600}>
                        {t('Événements passés')}
                      </Typography>
                      <IconButtonOutlined onClick={() => setOpenPast(!openPast)}>
                        {openPast ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButtonOutlined>
                    </Stack>

                    {openPast && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap="1rem"
                        padding={'1rem'}
                        style={{
                          backgroundColor: theme.colors.greyBackground,
                        }}
                      >
                        {past.map(event => (
                          <EventsCard key={event.id} event={event} isPast />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </Stack>
    </>
  )
}

export default Dashboard
